/**
 * Cookie-backed storage adapter for the Supabase auth client.
 *
 * Why: the blog (aicodingpatterns.com) and learn (learn.aicodingpatterns.com)
 * share one Supabase project. To share the LOGIN session across the two
 * subdomains, the session must live in a cookie scoped to the parent domain
 * (`.aicodingpatterns.com`) instead of per-origin localStorage. We inject this
 * adapter as `auth.storage` on a plain `createClient` so the implicit auth flow
 * and every existing auth behavior stay unchanged — only WHERE the bytes live
 * changes.
 *
 * Handles:
 *  - chunking: a Supabase session JSON can exceed the ~4KB single-cookie limit,
 *    so large values are split across `<key>.0`, `<key>.1`, … and recombined on
 *    read (decode happens ONCE after recombining, so a %XX escape split across a
 *    chunk boundary is never corrupted);
 *  - stale-chunk cleanup on overwrite/remove (a leftover chunk = corrupt parse =
 *    ghost session);
 *  - SSR/build safety: no-ops when `document` is undefined (Astro builds pages in
 *    Node; the same module is imported by the blog).
 */

const MAX_CHUNK = 3600 // encoded chars per cookie value — safely under the ~4KB limit (incl. name + attrs)

export interface CookieStorageOptions {
  /** Cookie domain, e.g. ".aicodingpatterns.com". Omit for a host-only cookie (dev). */
  domain?: string
  /** Set the Secure flag (prod/HTTPS only — Secure cookies are dropped on http://localhost). */
  secure?: boolean
  sameSite?: 'lax' | 'strict' | 'none'
  /** Cookie lifetime in seconds. Default ≈ 400 days (Chrome's max); validity is still enforced server-side by token expiry. */
  maxAge?: number
}

function isBrowser(): boolean {
  return typeof document !== 'undefined'
}

function readJar(): Record<string, string> {
  const out: Record<string, string> = {}
  for (const part of document.cookie ? document.cookie.split(';') : []) {
    const idx = part.indexOf('=')
    if (idx === -1) continue
    const k = part.slice(0, idx).trim()
    if (k) out[k] = part.slice(idx + 1).trim()
  }
  return out
}

export interface SupabaseCookieStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export function createCookieStorage(opts: CookieStorageOptions = {}): SupabaseCookieStorage {
  const { domain, secure = false, sameSite = 'lax', maxAge = 60 * 60 * 24 * 400 } = opts

  const attrs = (extra: string): string => {
    let s = '; Path=/'
    s += `; SameSite=${sameSite}`
    if (domain) s += `; Domain=${domain}`
    if (secure) s += '; Secure'
    return s + extra
  }

  const writeCookie = (name: string, value: string): void => {
    document.cookie = `${name}=${value}${attrs(`; Max-Age=${maxAge}`)}`
  }

  // Delete a cookie across every domain scope it might have been written under:
  // the configured domain, host-only, and the registrable parent of the current
  // host (e.g. ".aicodingpatterns.com" from "aicodingpatterns.com"). This makes
  // logout robust even if one app (e.g. a statically-built blog) was deployed
  // WITHOUT the cookie-domain env — it can still clear the shared domain cookie
  // the other app set. Deletion matches on name+domain+path only, so the extra
  // attempts are harmless no-ops when nothing matches.
  const deletionDomains = (): (string | undefined)[] => {
    const list: (string | undefined)[] = [domain, undefined]
    if (typeof location !== 'undefined') {
      const parts = location.hostname.split('.')
      if (parts.length >= 2) list.push('.' + parts.slice(-2).join('.'))
    }
    return Array.from(new Set(list))
  }

  const deleteCookie = (name: string): void => {
    for (const d of deletionDomains()) {
      document.cookie = `${name}=; Path=/${d ? `; Domain=${d}` : ''}; Max-Age=0`
    }
  }

  function removeItem(key: string): void {
    if (!isBrowser()) return
    const jar = readJar()
    if (jar[key] !== undefined) deleteCookie(key)
    let i = 0
    while (jar[`${key}.${i}`] !== undefined) {
      deleteCookie(`${key}.${i}`)
      i++
    }
  }

  function getItem(key: string): string | null {
    if (!isBrowser()) return null
    const jar = readJar()
    try {
      // Prefer chunks when present so a stray bare-key cookie (e.g. set by a
      // sibling subdomain) can't shadow the real chunked value.
      if (jar[`${key}.0`] !== undefined) {
        let combined = ''
        let i = 0
        while (jar[`${key}.${i}`] !== undefined) {
          combined += jar[`${key}.${i}`]
          i++
        }
        return decodeURIComponent(combined)
      }
      if (jar[key] !== undefined) return decodeURIComponent(jar[key])
      return null
    } catch {
      // A malformed/garbage cookie (e.g. a lone "%") must never throw into
      // supabase-js init — that would brick session resolution on every page.
      // Clear it and behave as logged-out (self-heal).
      removeItem(key)
      return null
    }
  }

  function setItem(key: string, value: string): void {
    if (!isBrowser()) return
    // Clear any previous representation (single value AND chunks) so a shrink
    // never leaves a stale trailing chunk behind.
    removeItem(key)
    const encoded = encodeURIComponent(value)
    if (encoded.length <= MAX_CHUNK) {
      writeCookie(key, encoded)
      return
    }
    let i = 0
    for (let pos = 0; pos < encoded.length; pos += MAX_CHUNK) {
      writeCookie(`${key}.${i}`, encoded.slice(pos, pos + MAX_CHUNK))
      i++
    }
  }

  return { getItem, setItem, removeItem }
}
